import { supabase } from '@/lib/supabase/client';

/**
 * Compare Florida vs Georgia data to see why FL shows but GA doesn't
 */

export async function compareFLvsGA() {
  console.log('🔍 Comparing Florida vs Georgia data processing...');

  try {
    // 1. Check raw data counts for both states
    console.log('📊 Step 1: Checking raw data counts...');

    const [flResult, gaResult] = await Promise.all([
      supabase
        .from('cars_with_safety_recalls_mv')
        .select('*', { count: 'exact', head: true })
        .eq('seller_region', 'FL'),
      supabase
        .from('cars_with_safety_recalls_mv')
        .select('*', { count: 'exact', head: true })
        .eq('seller_region', 'GA')
    ]);

    console.log(`📍 Florida records: ${flResult.count || 0}`);
    console.log(`📍 Georgia records: ${gaResult.count || 0}`);

    if ((flResult.count || 0) === 0 && (gaResult.count || 0) === 0) {
      console.log('❌ No data found for either state');
      return;
    }

    // 2. Get sample data for comparison
    console.log('\n📋 Step 2: Comparing sample data structure...');

    const [flSample, gaSample] = await Promise.all([
      supabase
        .from('cars_with_safety_recalls_mv')
        .select(
          `
          service_provider_name,
          seller_latitude,
          seller_longitude,
          seller_city,
          seller_region,
          vin,
          safety_recalls
        `
        )
        .eq('seller_region', 'FL')
        .limit(10),
      supabase
        .from('cars_with_safety_recalls_mv')
        .select(
          `
          service_provider_name,
          seller_latitude,
          seller_longitude,
          seller_city,
          seller_region,
          vin,
          safety_recalls
        `
        )
        .eq('seller_region', 'GA')
        .limit(10)
    ]);

    console.log(`📋 FL sample data: ${flSample.data?.length || 0} records`);
    console.log(`📋 GA sample data: ${gaSample.data?.length || 0} records`);

    // 3. Compare coordinate ranges
    console.log('\n🗺️ Step 3: Comparing coordinate ranges...');

    if (flSample.data && flSample.data.length > 0) {
      const flLats = flSample.data.map(r => r.seller_latitude).filter(lat => lat !== null);
      const flLngs = flSample.data.map(r => r.seller_longitude).filter(lng => lng !== null);

      console.log('📍 FL coordinate ranges:');
      console.log(`   Latitude: ${Math.min(...flLats)} to ${Math.max(...flLats)}`);
      console.log(`   Longitude: ${Math.min(...flLngs)} to ${Math.max(...flLngs)}`);
    }

    if (gaSample.data && gaSample.data.length > 0) {
      const gaLats = gaSample.data.map(r => r.seller_latitude).filter(lat => lat !== null);
      const gaLngs = gaSample.data.map(r => r.seller_longitude).filter(lng => lng !== null);

      console.log('📍 GA coordinate ranges:');
      console.log(`   Latitude: ${Math.min(...gaLats)} to ${Math.max(...gaLats)}`);
      console.log(`   Longitude: ${Math.min(...gaLngs)} to ${Math.max(...gaLngs)}`);
    }

    // 4. Test the exact spatial clustering query for both states
    console.log('\n🗺️ Step 4: Testing spatial clustering queries...');

    // Florida bounds (known to work)
    const FL_BOUNDS = {
      north: 31.0,
      south: 24.5,
      east: -79.5,
      west: -87.6
    };

    // Georgia bounds
    const GA_BOUNDS = {
      north: 35.0,
      south: 30.3,
      east: -80.8,
      west: -85.6
    };

    const [flSpatial, gaSpatial] = await Promise.all([
      supabase
        .from('cars_with_safety_recalls_mv')
        .select(
          `
          service_provider_name,
          seller_latitude,
          seller_longitude,
          seller_city,
          seller_region,
          vin,
          safety_recalls
        `
        )
        .gte('seller_latitude', FL_BOUNDS.south)
        .lte('seller_latitude', FL_BOUNDS.north)
        .gte('seller_longitude', FL_BOUNDS.west)
        .lte('seller_longitude', FL_BOUNDS.east)
        .not('seller_latitude', 'is', null)
        .not('seller_longitude', 'is', null)
        .limit(50),
      supabase
        .from('cars_with_safety_recalls_mv')
        .select(
          `
          service_provider_name,
          seller_latitude,
          seller_longitude,
          seller_city,
          seller_region,
          vin,
          safety_recalls
        `
        )
        .gte('seller_latitude', GA_BOUNDS.south)
        .lte('seller_latitude', GA_BOUNDS.north)
        .gte('seller_longitude', GA_BOUNDS.west)
        .lte('seller_longitude', GA_BOUNDS.east)
        .not('seller_latitude', 'is', null)
        .not('seller_longitude', 'is', null)
        .limit(50)
    ]);

    console.log(`🗺️ FL spatial query: ${flSpatial.data?.length || 0} records`);
    console.log(`🗺️ GA spatial query: ${gaSpatial.data?.length || 0} records`);

    // Show what states are actually in each spatial query
    if (flSpatial.data && flSpatial.data.length > 0) {
      const flStates = [...new Set(flSpatial.data.map(r => r.seller_region))];
      console.log(`🗺️ FL spatial query contains states: ${flStates.join(', ')}`);

      const flInBounds = flSpatial.data.filter(r => r.seller_region === 'FL');
      console.log(`🗺️ FL records in FL bounds: ${flInBounds.length}`);
    }

    if (gaSpatial.data && gaSpatial.data.length > 0) {
      const gaStates = [...new Set(gaSpatial.data.map(r => r.seller_region))];
      console.log(`🗺️ GA spatial query contains states: ${gaStates.join(', ')}`);

      const gaInBounds = gaSpatial.data.filter(r => r.seller_region === 'GA');
      console.log(`🗺️ GA records in GA bounds: ${gaInBounds.length}`);
    }

    // 5. Compare safety_recalls structure
    console.log('\n🔍 Step 5: Comparing safety_recalls structure...');

    if (flSample.data && flSample.data.length > 0) {
      const flRecord = flSample.data[0];
      console.log('📋 FL safety_recalls example:');
      console.log(`   Type: ${typeof flRecord.safety_recalls}`);
      if (flRecord.safety_recalls) {
        if (Array.isArray(flRecord.safety_recalls)) {
          console.log(`   Array length: ${flRecord.safety_recalls.length}`);
          if (flRecord.safety_recalls.length > 0) {
            console.log('   Sample recall:', flRecord.safety_recalls[0]);
          }
        } else if (typeof flRecord.safety_recalls === 'string') {
          try {
            const parsed = JSON.parse(flRecord.safety_recalls);
            console.log(`   Parsed length: ${parsed?.length || 0}`);
          } catch (e) {
            console.log(`   Parse error: ${e.message}`);
          }
        }
      }
    }

    if (gaSample.data && gaSample.data.length > 0) {
      const gaRecord = gaSample.data[0];
      console.log('📋 GA safety_recalls example:');
      console.log(`   Type: ${typeof gaRecord.safety_recalls}`);
      if (gaRecord.safety_recalls) {
        if (Array.isArray(gaRecord.safety_recalls)) {
          console.log(`   Array length: ${gaRecord.safety_recalls.length}`);
          if (gaRecord.safety_recalls.length > 0) {
            console.log('   Sample recall:', gaRecord.safety_recalls[0]);
          }
        } else if (typeof gaRecord.safety_recalls === 'string') {
          try {
            const parsed = JSON.parse(gaRecord.safety_recalls);
            console.log(`   Parsed length: ${parsed?.length || 0}`);
          } catch (e) {
            console.log(`   Parse error: ${e.message}`);
          }
        }
      }
    }

    // 6. Test M/N filtering on both states
    console.log('\n🚫 Step 6: Testing M/N campaign filtering...');

    const testMNFiltering = (data: any[], stateName: string) => {
      let totalRecalls = 0;
      let mnRecalls = 0;
      let validRecalls = 0;

      data.forEach(row => {
        if (row.safety_recalls && Array.isArray(row.safety_recalls)) {
          row.safety_recalls.forEach((recall: any) => {
            totalRecalls++;
            const campaignNumber = recall.campaign_number || '';
            if (campaignNumber.includes('M') || campaignNumber.includes('N')) {
              mnRecalls++;
            } else {
              validRecalls++;
            }
          });
        }
      });

      console.log(`🚫 ${stateName} M/N filtering results:`);
      console.log(`   Total recalls: ${totalRecalls}`);
      console.log(`   M/N recalls (filtered out): ${mnRecalls}`);
      console.log(`   Valid recalls (kept): ${validRecalls}`);

      return { totalRecalls, mnRecalls, validRecalls };
    };

    if (flSample.data && flSample.data.length > 0) {
      testMNFiltering(flSample.data, 'FL');
    }

    if (gaSample.data && gaSample.data.length > 0) {
      testMNFiltering(gaSample.data, 'GA');
    }

    return {
      flRecords: flResult.count || 0,
      gaRecords: gaResult.count || 0,
      flSpatialResults: flSpatial.data?.length || 0,
      gaSpatialResults: gaSpatial.data?.length || 0
    };
  } catch (error) {
    console.error('❌ Comparison failed:', error);
    return null;
  }
}

// Export to window for console access
if (typeof window !== 'undefined') {
  (window as any).compareFLvsGA = compareFLvsGA;
}

export default compareFLvsGA;
